import Axios, { AxiosInstance, AxiosPromise, CancelToken } from 'axios'
import applyConverters from 'axios-case-converter'
import * as Sentry from '@sentry/browser'
import { PackableItem } from './routes/orders/packing/types'
import { IOrdersMetaState } from './routes/orders/types'
import * as API from './types/api'
import {
  CategoryListingConfiguration,
  DimWeightCalculatorParams,
  GoodwillfindsListing,
  EtsyListing,
  GoodwillFindsMarketAccount,
  GoodwillFindsSettings,
  EtsySettings,
  PaginableMeta,
  ProductV4
} from './types/api'
import { RawProductListing } from './routes/product_manager/components/ListingHistorySection/types'
import {
  DepopSizeSet,
  DepopSpecific,
  DepopSpecificValue,
  MarketplaceServiceSpecific,
  MarketplaceServiceSupplier
} from './routes/products_v5/components/JoinedForm/types'
import { constructQueryParamString } from './utils/queryParamString'
import { AmazonListing, DepopListing } from './types/api/listings'
import { EntrupyAuthenticationParams, EntrupyAuthenticationResponse } from './types/api/entrupy'

const csrfElement: HTMLMetaElement = document.querySelector('meta[name="csrf-token"]')

const baseConfig = {
  baseURL: '/api',
  timeout: 60 * 1000,
  headers: {
    'X-CSRF-Token': csrfElement.content,
    'X-SOURCE-VERSION': import.meta.env.VITE_SOURCE_VERSION // This env is added by Heroku when the source builds
  }
}

export type AddAdditionalDetailsChannelNames = 'ebay' | 'shopgoodwill'

export const baseClient: AxiosInstance = applyConverters(Axios.create(baseConfig))

// Middleware to store the version the server claims it's running on every request
baseClient.interceptors.response.use(response => {
  try {
    localStorage.setItem('serverVersion', response?.headers?.xServerVersion)
  } catch (e) {
    Sentry.captureException(e)
  }
  return response
})

// client/constants/Channels.ts already imports Client, so we can't import Channels.ts, and I don't want to refactor right now
const CHANNELS = {
  SHOPGOODWILL: { id: 1 },
  EBAY: { id: 2 },
  OFFER_UP: { id: 3 },
  FACEBOOK: { id: 7 },
  BLUEBOX: { id: 8 },
  SHOPIFY: { id: 9 },
  GOODWILLFINDS: { id: 10 },
  ETSY: { id: 11 },
  AMAZON: { id: 12 }
}

export class Client {
  // When typing response types, the fewer type dependencies, the better.
  // 1) great inline documentation
  // 2) Make sure it's easy to modify and understand
  // - you don't want to have to update another component's typing just to get the client to work
  //
  // Consider two endpoints, both returning an array of users.
  // 1) users: { id: number, name: string }
  // 2) users: { id: number, name: string, username: string }
  // Instead of using optional properties, using separate types entirely will greatly reduce possibility of error!

  // Highlighted examples:

  static getFacebookSettings(): AxiosPromise<{
    facebookSettings: API.FacebookSettings
  }> {
    return baseClient.get('/settings/facebook')
  }

  static getFacebookAccount(): AxiosPromise<{
    userAccount: API.FacebookUserAccount
    organizationAccount: API.FacebookOrganizationAccount
    facebookSettings: API.FacebookSettings
    asyncJob?: API.AsyncJob
  }> {
    return baseClient.get('/market_accounts/facebook')
  }

  // example with typed parameter

  static manifestItemAccept(data: { manifestItem: { ids: number[] } }): AxiosPromise<{
    manifest: API.Manifest
    failedIds: number[]
    successfulIds: number[]
  }> {
    return baseClient.put(`v5/manifest_items/accept`, data)
  }

  // End highlighted examples

  static presign(contentType): AxiosPromise {
    return baseClient.get(`/images/presign?type=${contentType}`)
  }

  static presignOrganizationImage(contentType): AxiosPromise {
    return Axios.create({ ...baseConfig }).get(`/organizations/images/presign?type=${contentType}`)
  }

  static presignV5(contentType): AxiosPromise {
    return Axios.create({ ...baseConfig }).get(`/v5/images/presign?type=${contentType}`)
  }

  static presignItemAuthenticationImage(contentType): AxiosPromise {
    return Axios.create({ ...baseConfig }).get(`/v5/item_authentications/images/presign?type=${contentType}`)
  }

  static uploadS3(url, contentType, data, onUploadProgress = () => {}): AxiosPromise {
    return Axios.put(url, data, { onUploadProgress, headers: { 'Content-Type': contentType } })
  }

  static uploadS3V2(
    url,
    fields,
    contentType,
    data,
    onUploadProgress: (progressEvent: any) => void = () => {}
  ): AxiosPromise {
    const formData = new FormData()
    Object.keys(fields).forEach(key => {
      formData.append(key, fields[key])
    })
    formData.append('file', data)

    return Axios({
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      url,
      onUploadProgress
    })
  }

  static getProduct(id): AxiosPromise {
    return baseClient.get(`/v2/products/${id}`)
  }

  static createProduct(data): AxiosPromise {
    return baseClient.post(`/v2/products`, data)
  }

  static updateProduct(id, data): AxiosPromise {
    return baseClient.put(`/v2/products/${id}`, data)
  }

  static deleteProduct(productId): AxiosPromise {
    return baseClient.delete(`/v2/products/${productId}`)
  }

  static asyncJob(asyncJobId): AxiosPromise {
    return baseClient.get(`/async_jobs/${asyncJobId}`)
  }

  static createLegacyProduct(data): AxiosPromise {
    return baseClient.post(`/legacy_products`, data)
  }

  static updateLegacyProduct(productId, data): AxiosPromise {
    return baseClient.put(`/legacy_products/${productId}`, data)
  }

  static deleteLegacyProduct(productId): AxiosPromise {
    return baseClient.delete(`/legacy_products/${productId}`)
  }

  static listings(meta: any = { page: 1, per_page: 10, userId: null, state: null }): AxiosPromise {
    return baseClient.get(`/listings`, {
      params: {
        ...meta,
        userId: meta.userId === 'all' ? null : meta.userId,
        state: meta.state === 'all' ? null : meta.state,
        sellerPrivateDescription: meta.sellerPrivateDescription === '' ? null : meta.sellerPrivateDescription,
        title: meta.title === '' ? null : meta.title,
        createdAt: meta && (meta.createdAt[0] == null || meta.createdAt[1] == null) ? null : meta.createdAt
      }
    })
  }

  static getListing(listingId): AxiosPromise {
    return baseClient.get(`/legacy_products/${listingId}`)
  }

  static updateListing(id, data): AxiosPromise {
    return baseClient.put(`/listings/${id}`, data)
  }

  static deleteListing(id): AxiosPromise {
    return baseClient.delete(`/listings/${id}`)
  }

  static overviewReport(timeframe): AxiosPromise {
    return baseClient.get(`/reports/overview?timeframe=${timeframe}`)
  }

  static validateListing(data): AxiosPromise {
    return baseClient.post(`/shopgoodwill_listings/validate`, data)
  }

  static getShipments(data): AxiosPromise {
    return baseClient.get('/shipments', data)
  }

  static createListing(data): AxiosPromise {
    return baseClient.post(`/listings`, data)
  }

  static preview(data): AxiosPromise {
    return baseClient.post(`/preview`, data)
  }

  static status(): AxiosPromise {
    return baseClient.get('/status')
  }

  static users(params?: any): AxiosPromise {
    const safeParams = params !== null && typeof params === 'object' ? params : {}
    return baseClient.get('/users', { params: safeParams })
  }

  static usersExceptSelf(): AxiosPromise {
    const proxyWindow: any = window
    return baseClient.get(`/users?except=${proxyWindow.user.id}`)
  }

  static getUser(userId): AxiosPromise {
    return baseClient.get(`/users/${userId}`)
  }

  static deleteUser(userId): AxiosPromise {
    return baseClient.put(`/users/${userId}`, { user: { active: false } })
  }

  static createUser(data): AxiosPromise {
    return baseClient.post(`/users`, { user: data })
  }

  static getAPIOnlyUsers(): AxiosPromise {
    return baseClient.get(`/users/api_only`)
  }

  static getPearldiveApiTokens(): AxiosPromise {
    return baseClient.get(`/users/api_only/fetch_pearldive_tokens`)
  }

  static createPearldiveApiTokens(): AxiosPromise {
    return baseClient.post(`/users/api_only/create_pearldive_api_tokens`)
  }

  static getFacebookPageFeeds(): AxiosPromise {
    return baseClient.get(`/markets/facebook/page_feeds`)
  }

  static createAPIOnlyUser(data): AxiosPromise {
    return baseClient.post(`/users/api_only`, { user: data })
  }

  static refreshAPIOnlyUserToken(id, tokenPrefix: string): AxiosPromise {
    return baseClient.put(`/users/api_only/${id}/token`, { tokenPrefix })
  }

  static updateUser(id, data): AxiosPromise {
    return baseClient.put(`/users/${id}`, { user: data })
  }

  static unlockUserAccount(userId: number): AxiosPromise {
    return baseClient.post(`/users/${userId}/unlock_account`)
  }

  static resetUserMfa(userId: number): AxiosPromise {
    return baseClient.post(`/users/${userId}/reset_mfa`)
  }

  static getUserStats(): AxiosPromise {
    return baseClient.get(`/users-stats`)
  }

  static getOrganizationSettings(): AxiosPromise {
    return baseClient.get('/organizations/settings')
  }

  static updateOrganizationSettings(data): AxiosPromise {
    return baseClient.put(`/organizations/settings/`, data)
  }

  static getPickLists(params): AxiosPromise<{ pickLists: [API.PickList]; meta: any }> {
    return baseClient.get(`/pick_lists`, { params })
  }

  static getPickList(picklistId): AxiosPromise<{ pickList: API.PickList }> {
    return baseClient.get(`/pick_lists/${picklistId}`)
  }

  static generatePickList(pickingProfileId: string, itemsToPick: string): AxiosPromise<{ pickList: API.PickList }> {
    return baseClient.post(`/link/orders/pick_lists/generate_by_filters`, {
      pickingProfileId,
      itemsToPick,
      sort: 'paid_at.asc' // gives preference to oldest items first
    })
  }

  static generatePickListPreview(filters) {
    return baseClient.post(`/pick_lists/preview`, { pick_list_filters: filters })
  }

  static unlockPickList(id: number): AxiosPromise<{ message?: string }> {
    return baseClient.put(`/pick_lists/${id}/unlock`)
  }

  static unlockAllPickLists(): AxiosPromise<{}> {
    return baseClient.post('/pick_lists/unlock_all')
  }

  static getPickingProfiles(): AxiosPromise<{ pickingProfiles: [API.PickingProfile] }> {
    return baseClient.get(`/picking_profiles`)
  }

  static getPickingProfile(id): AxiosPromise<{ pickingProfile: API.PickingProfile }> {
    return baseClient.get(`/picking_profiles/${id}`)
  }

  static updatePickingProfile(
    id,
    data: API.PickingProfileUpdate
  ): AxiosPromise<{ pickingProfile: API.PickingProfile }> {
    return baseClient.put(`/picking_profiles/${id}`, { picking_profile: data })
  }

  static createPickingProfile(
    id,
    data: API.PickingProfileCreate
  ): AxiosPromise<{ pickingProfile: API.PickingProfile }> {
    return baseClient.post(`/picking_profiles`, { picking_profile: data })
  }

  static deletePickingProfile(id): AxiosPromise<{ pickingProfiles: [API.PickingProfile] }> {
    return baseClient.delete(`/picking_profiles/${id}`)
  }

  static getShopgoodwillSettings(): AxiosPromise {
    return baseClient.get(`/settings/shopgoodwill`)
  }

  static updateShopgoodwillSettings(data): AxiosPromise {
    return baseClient.put(`/settings/shopgoodwill`, data)
  }

  static getAllEbaySettings(
    params: { matchesSupplierId?: number } = {}
  ): AxiosPromise<{ ebaySettings: API.EbaySettingsResponse[] }> {
    return baseClient.get(`/v5/settings/ebay`, { params })
  }

  static getAllEtsySettings(): AxiosPromise<{ etsySettings: API.EtsySettingsResponse[] }> {
    return baseClient.get(`/settings/etsy/`)
  }

  static getEtsySettingsV1(settingsId: number): AxiosPromise<{
    settings: API.EtsySettings
    account: API.EtsyMarketAccount
  }> {
    return baseClient.get(`/settings/etsy/${settingsId}`)
  }

  static getAllDepopSettings(): AxiosPromise<{ depopSettings: API.DepopSettingsResponse[] }> {
    return baseClient.get(`/settings/depop/`)
  }

  static getDepopSettingsV1(settingsId: number): AxiosPromise<{
    settings: API.DepopSettings
    account: API.DepopMarketAccount
  }> {
    return baseClient.get(`/settings/depop/${settingsId}`)
  }

  static getEbaySettingsV2(settingsId: number): AxiosPromise<{
    settings: API.EbaySettings
    account: API.EbayMarketAccount
  }> {
    return baseClient.get(`/v5/settings/ebay/${settingsId}`)
  }

  static updateEbaySettingsV2(
    id,
    params: { settings: API.EbaySettings; account: API.EbayMarketAccount }
  ): AxiosPromise<{ settings: API.EbaySettings; account: API.EbayMarketAccount }> {
    return baseClient.put(`/v5/settings/ebay/${id}`, params)
  }

  static getAmazonSettings(): AxiosPromise {
    return baseClient.get('/settings/amazon')
  }

  static getAllAmazonSettings(): AxiosPromise<{ amazonSettings: API.AmazonSettingsResponse[] }> {
    return baseClient.get(`/settings/amazon/`)
  }

  static getAmazonSettingsV1(settingsId: number): AxiosPromise<{
    settings: API.AmazonSettings
    account: API.AmazonMarketAccount
  }> {
    return baseClient.get(`/settings/amazon/${settingsId}`)
  }

  static updateFacebookSettings(facebookSettings): AxiosPromise {
    return baseClient.put('/settings/facebook', { facebookSettings })
  }

  static getOfferUpSettings(): AxiosPromise {
    return baseClient.get(`/settings/offer_up`)
  }

  static updateOfferUpSettings(data): AxiosPromise {
    return baseClient.put(`/settings/offer_up`, data)
  }

  static getUserNoficiationSettings(): AxiosPromise {
    return baseClient.get('settings/user_notification')
  }

  static updateUserNoficiationSettings(data): AxiosPromise {
    return baseClient.put('settings/user_notification', data)
  }

  static getEasyPostAccount(): AxiosPromise {
    return baseClient.get('/easy_post_account')
  }

  static createEasyPostAccount(easyPostAccountToken): AxiosPromise {
    return baseClient.post('/easy_post_account', { easyPostAccountToken })
  }

  static deleteEasyPostAccount(): AxiosPromise {
    return baseClient.delete('/easy_post_account')
  }

  static getUserSettings(): AxiosPromise {
    return baseClient.get('/users/settings')
  }

  static updateUserSettings(data): AxiosPromise {
    return baseClient.put('/users/settings', data)
  }

  static getListingsForProduct(productId, params): AxiosPromise {
    return baseClient.get(`/v4/products/${productId}/listings`, { params })
  }

  static markOrdersPrinted(orderIds): AxiosPromise {
    return baseClient.put('/bulk/print', { orderIds })
  }

  // Powers the "Poster overview" reports tab.
  static reportsBetween(timeStart, timeEnd): AxiosPromise {
    return baseClient.get(`/reports/between?time_start=${timeStart}&time_end=${timeEnd}`)
  }

  static userOverviewReport(userId, params): AxiosPromise {
    return baseClient.get(`/reports/users/${userId}`, { params })
  }

  static forceRelist(listingId): AxiosPromise {
    return baseClient.put(`/listings/${listingId}/force_relist`)
  }

  static templateIndex(params = {}): AxiosPromise {
    return baseClient.get(`/templates`, { params })
  }

  static getTemplate(id): AxiosPromise {
    return baseClient.get(`/templates/${id}`)
  }

  static createTemplate(template): AxiosPromise {
    return baseClient.post(`/templates`, { template })
  }

  static updateTemplate(id, body): AxiosPromise {
    return baseClient.put(`/templates/${id}`, { template: body })
  }

  static deleteTemplate(id): AxiosPromise {
    return baseClient.delete(`/templates/${id}`)
  }

  static duplicateTemplate(id): AxiosPromise {
    return baseClient.post(`/templates/${id}/duplicate`)
  }

  static createShipmentRates(shipment, cancelToken): AxiosPromise {
    return baseClient.post('/shipments/rate', { shipment }, { cancelToken })
  }

  static quickshipBuyShipmentLabel(
    externalShipmentId,
    externalRateId,
    orderId,
    externalOrderId,
    shipmentParcels,
    carrier,
    carrierService
  ): AxiosPromise {
    return baseClient.post('/shipments/buy_label', {
      externalShipmentId,
      externalRateId,
      orderId,
      externalOrderId,
      shipmentParcels,
      carrier,
      carrierService
    })
  }

  static calculateShipment(shipment): AxiosPromise {
    return baseClient.post('/shipments/calculate', { shipment })
  }

  static refundShipment(shipmentId): AxiosPromise {
    return baseClient.post(`shipments/${shipmentId}/refund`)
  }

  static bulkRefundShipments(data): AxiosPromise {
    return baseClient.post(`shipments/bulk_refund`, data)
  }

  static buyShipmentLabel(shipmentId, rateId): AxiosPromise {
    return baseClient.post(`/shipments/${shipmentId}/label`, { rateId })
  }

  static shippingBoxIndex(): AxiosPromise {
    return baseClient.get('/shipping_boxes')
  }

  static getShippingBox(id): AxiosPromise<{
    shippingBox: API.ShippingBox
  }> {
    return baseClient.get(`/shipping_boxes/${id}`)
  }

  static createShippingBox(body): AxiosPromise {
    return baseClient.post(`/shipping_boxes`, body)
  }

  static updateShippingBox(id, body): AxiosPromise {
    return baseClient.put(`/shipping_boxes/${id}`, body)
  }

  static deleteShippingBox(id): AxiosPromise {
    return baseClient.delete(`/shipping_boxes/${id}`)
  }

  static updateShippingBoxScanSheetOption(id, scanSheetEnabled): AxiosPromise {
    return baseClient.put(`/shipping_boxes/${id}/update_scan_sheet_option`, { shipping_box: { scanSheetEnabled } })
  }

  static syncShippingBoxes(): AxiosPromise {
    return baseClient.put(`/shipping_boxes/sync`)
  }

  static calculateBestShippingBox(length, width, height, padding): AxiosPromise {
    return baseClient.post('/shipping_boxes/calculate_best_box', { length, width, height, padding })
  }

  static syncSellerStores(): AxiosPromise {
    return baseClient.put(`/seller_stores/sync`)
  }

  static getMarketAccounts(): AxiosPromise {
    return baseClient.get(`/market_accounts`)
  }

  static createFacebookAccount(systemUserToken): AxiosPromise {
    return baseClient.post('/market_accounts/facebook', { systemUserToken })
  }

  static disconnectUserFacebookAccount(): AxiosPromise {
    return baseClient.delete('/market_accounts/facebook/user')
  }

  static resyncFacebookAccount(): AxiosPromise {
    return baseClient.put('/market_accounts/facebook')
  }

  static enableMarketAccounts(marketAccounts): AxiosPromise {
    return baseClient.put(`/market_accounts/enable`, { marketAccounts })
  }

  static getMarketAccountsShopgoodwill(owner): AxiosPromise {
    return baseClient.get(`/market_accounts/shopgoodwill`, { params: { owner } })
  }

  static marketAccountsShopgoodwill(username, password, owner): AxiosPromise {
    return baseClient.post(`/market_accounts/shopgoodwill`, { username, password, owner })
  }

  static marketAccountsAPIShopgoodwill(marketAccount): AxiosPromise {
    return baseClient.put(`/market_accounts/shopgoodwill`, { marketAccount })
  }

  static getMarketAccountsGoodwillFinds(): AxiosPromise<{ marketAccount: GoodwillFindsMarketAccount }> {
    return baseClient.get(`/market_accounts/goodwillfinds`)
  }

  static getGoodwillFindsSettings(marketAccountId): AxiosPromise<{ settings: GoodwillFindsSettings }> {
    return baseClient.get(`/settings/goodwillfinds/${marketAccountId}`)
  }

  static getEtsysSettings(marketAccountId): AxiosPromise<{ settings: EtsySettings }> {
    return baseClient.get(`/settings/etsy/${marketAccountId}`)
  }

  static updateGoodwillFindsSettings(marketAccountId, settings): AxiosPromise<{ settings: GoodwillFindsSettings }> {
    return baseClient.put(`/settings/goodwillfinds/${marketAccountId}`, { settings })
  }

  static marketAccountsOfferUp(username, password): AxiosPromise {
    return baseClient.post(`/market_accounts/offer_up`, { username, password })
  }

  static inventoryLocationIndex(): AxiosPromise {
    return baseClient.get(`/inventory_locations`)
  }

  static getInventoryLocation(id): AxiosPromise {
    return baseClient.get(`/inventory_locations/${id}`)
  }

  static createInventoryLocation(inventoryLocation): AxiosPromise {
    return baseClient.post(`/inventory_locations`, { inventoryLocation })
  }

  static updateInventoryLocation(id, inventoryLocation): AxiosPromise {
    return baseClient.put(`/inventory_locations/${id}`, { inventoryLocation })
  }

  static deleteInventoryLocation(id): AxiosPromise {
    return baseClient.delete(`/inventory_locations/${id}`)
  }

  static bulkDeleteInventoryLocations(params): AxiosPromise {
    return baseClient.delete(`/inventory_locations/bulk_destroy`, { params })
  }

  static bulUpdateToRetailsInventoryLocations(params): AxiosPromise {
    return baseClient.patch(`/inventory_locations/bulk_update_retail`, { params })
  }

  static syncInventoryLocations(): AxiosPromise {
    return baseClient.put(`/inventory_locations/sync`)
  }

  static productIndex(meta: any = {}): AxiosPromise {
    return baseClient.get(`/v2/products`, {
      params: {
        ...meta,
        cursorPaginate: true,
        userId: meta.userId === 'all' ? null : meta.userId,
        sku: meta.sku === '' ? null : meta.sku,
        title: meta.title === '' ? null : meta.title,
        createdAt:
          meta && meta.createdAt && (meta.createdAt[0] == null || meta.createdAt[1] == null) ? null : meta.createdAt
      }
    })
  }

  static getUserStatsV2(): AxiosPromise {
    return baseClient.get('/v2/stats')
  }

  static getProductListings(productId): AxiosPromise {
    return baseClient.get(`/v2/products/${productId}/listings`)
  }

  static createProductListing(productId, market, form): AxiosPromise {
    return baseClient.post(`/v2/products/${productId}/listings/${market}`, { [market]: form })
  }

  static editProductListing(productId, listingId, market, form): AxiosPromise {
    return baseClient.put(`/v2/products/${productId}/listings/${market}/${listingId}`, { [market]: form })
  }

  static deleteProductListing(productId, listingId, market): AxiosPromise {
    return baseClient.delete(`/v2/products/${productId}/listings/${market}/${listingId}`)
  }

  static relistProductListing(productId, listingId, market): AxiosPromise {
    return baseClient.put(`/v2/products/${productId}/listings/${market}/${listingId}/relist`)
  }

  static getProductListing(productId, market, marketId): AxiosPromise {
    return baseClient.get(`/v2/products/${productId}/listings/${market}/${marketId}`)
  }

  static suppliersIndex(params: { listingStrategyId?: number } = {}): AxiosPromise<{
    suppliers: API.SupplierIndexSupplier[]
  }> {
    const safeParams = params !== null && typeof params === 'object' ? params : {}
    return baseClient.get(`/suppliers`, { params: safeParams })
  }

  static getSupplier(id): AxiosPromise {
    return baseClient.get(`/suppliers/${id}`)
  }

  static createSupplier(supplier): AxiosPromise {
    return baseClient.post(`/suppliers`, { supplier })
  }

  static updateSupplier(id, supplier): AxiosPromise {
    return baseClient.put(`/suppliers/${id}`, { supplier })
  }

  static deleteSupplier(id): AxiosPromise {
    return baseClient.delete(`/suppliers/${id}`)
  }

  static categoryIndex(marketIds: number[] | null = null): AxiosPromise {
    const params: Record<string, unknown> = {}

    // LoadableSelect passes typed search strings as the first argument; only treat numeric arrays as filters.
    if (Array.isArray(marketIds) && marketIds.every(id => typeof id === 'number')) {
      params.marketIds = marketIds
    }

    return baseClient.get('/v2/categories', { params })
  }

  static categoryV2Index(categoryId): AxiosPromise {
    return baseClient.get(`/v2/categories/tree?category_id=${categoryId}`)
  }

  static updateCategories(categories): AxiosPromise {
    return baseClient.put(`/v2/categories`, { categories })
  }

  static updateCategoryMappings(categoryId, category): AxiosPromise {
    return baseClient.put(`/v2/categories/${categoryId}/category_mappings`, { category })
  }

  static getCategory(categoryId): AxiosPromise {
    return baseClient.get(`/v2/categories/${categoryId}`)
  }

  static categoryChildren(marketId, id): AxiosPromise {
    // don't remove marketId, it prevents an argument error
    return baseClient.get(`/categories/${id}`)
  }

  static categoryConditions(id): AxiosPromise {
    return baseClient.get(`/markets/ebay/category_market_categories/${id}/conditions`)
  }

  static categorySpecifics(id): AxiosPromise {
    return baseClient.get(`/markets/ebay/category_market_categories/${id}/specifics`)
  }

  static goodwillFindsCategorySpecifics(categoryMarketCategoryId): AxiosPromise<{
    specifics: MarketplaceServiceSpecific[]
  }> {
    return baseClient.get(`/markets/goodwillfinds/category_market_categories/${categoryMarketCategoryId}/specifics`)
  }

  static goodwillFindsSuppliers(): AxiosPromise<{
    suppliers: MarketplaceServiceSupplier[]
  }> {
    return baseClient.get(`/markets/goodwillfinds/suppliers`)
  }

  // Depop's specifics are global rather than per-category, so this is fetched once per form.
  // Values are deliberately omitted — brand alone carries ~18k of them.
  static depopSpecifics(): AxiosPromise<{ specifics: DepopSpecific[] }> {
    return baseClient.get(`/markets/depop/specifics`)
  }

  static depopSpecificValues(specific: string): AxiosPromise<{
    valueRecommendations: DepopSpecificValue[]
  }> {
    return baseClient.get(`/markets/depop/specifics`, { params: { specific } })
  }

  static depopCategorySpecifics(categoryMarketCategoryId): AxiosPromise<{ specifics: DepopSpecific[] }> {
    return baseClient.get(`/markets/depop/specifics`, {
      params: { category_market_category_id: categoryMarketCategoryId }
    })
  }

  // Sizes are not specifics: Depop takes them as top-level size_set_id/size_id, and which sizes
  // exist depends on the category. Categories without sizes return an empty list.
  static depopCategorySizes(categoryMarketCategoryId): AxiosPromise<DepopSizeSet> {
    return baseClient.get(`/markets/depop/sizes`, {
      params: { category_market_category_id: categoryMarketCategoryId }
    })
  }

  static resetCategorySpecifics(id): AxiosPromise<{}> {
    return baseClient.delete(`/markets/ebay/category_market_categories/${id}/specifics`)
  }

  static categorySpecificRecommendations(id, specific): AxiosPromise {
    return baseClient.get(`/markets/ebay/category_market_categories/${id}/specifics?specific=${specific}`)
  }

  static categoryTree(id): AxiosPromise {
    return baseClient.get(`/categories/${id}/tree`)
  }

  static categoryMarketCategories(params = {}): AxiosPromise {
    return baseClient.get(`/v2/categories/market_categories`, { params })
  }

  static legacyShopgoodwillCategoriesTree(): AxiosPromise {
    return baseClient.get(`/legacy_shopgoodwill_categories`)
  }

  static carriersIndex(): AxiosPromise {
    return baseClient.get('/carriers')
  }

  static organizationCarrierAccountsIndex(): AxiosPromise {
    return baseClient.get('/organizations/carrier_accounts')
  }

  static updateOrganizationCarrierAccounts(params = {}): AxiosPromise {
    return baseClient.put('organizations/carrier_accounts', params)
  }

  static marketCategories(market, categoryId): AxiosPromise {
    return baseClient.get(`/markets/${market}/categories/${categoryId}/children`)
  }

  // TODO(multi-ebay-account): UL-1948 to remove this in favor of getEbaySellerProfilesV5
  // once this is removed please also delete app/controllers/api/markets/ebay/seller_profiles_controller.rb
  // Can remove soon after https://github.com/uprightlabs/lister/pull/8344 is deployed
  static getEbaySellerProfiles(): AxiosPromise {
    return baseClient.get(`/markets/ebay/seller_profiles`)
  }

  static getEbaySellerProfilesV5(marketAccountId): AxiosPromise<{ profiles: API.EbaySellerProfile[] }> {
    return baseClient.get(`/v5/ebay/seller_profiles`, { params: { marketAccountId } })
  }

  static updateEbaySellerProfiles(data): AxiosPromise {
    return baseClient.put(`/v4/bulk_update/ebay_seller_profiles`, data)
  }

  static syncEbaySellerProfilesV5(marketAccountId): AxiosPromise {
    return baseClient.post(`v5/market_accounts/${marketAccountId}/ebay_seller_profiles_sync`)
  }

  // onlyVisible - show only profiles/policies where hidden == false.  seller profiles (admin settings -> channel -> edit settings -> -> edit seller profile to change visibility
  static getMarketSellerProfilesV5(
    marketAccountId: number,
    onlyVisible = false
  ): AxiosPromise<{ shippingProfiles: API.MarketSellerProfile[]; returnPolicies: API.MarketSellerProfile[] }> {
    return baseClient.get(`/v5/common/market_seller_profiles`, {
      params: { marketAccountId, onlyVisible }
    })
  }

  static updateMarketSellerProfiles(data): AxiosPromise {
    return baseClient.put(`/v4/bulk_update/market_seller_profiles`, data)
  }

  static searchAmazonBooks(params: { query?: string; asin_query?: string }): AxiosPromise<{
    books: any[]
    sortDecision?: 'scan' | 'salvage' | 'multipleResults' | 'accept' | 'reject' | 'hot_list' | 'store' | 'ecommerce' // Backwards compatibility
  }> {
    return baseClient.get(`/books/search`, { params })
  }

  static syncMarketSellerProfilesV5(marketAccountId): AxiosPromise {
    return baseClient.post(`v5/market_accounts/${marketAccountId}/market_seller_profiles_sync`)
  }

  static getShopgoodwillListingStatus(productIds): AxiosPromise {
    return baseClient.get(`/v4/listings/shopgoodwill/status`, { params: { productIds } })
  }

  static getEbayListingStatus(productIds): AxiosPromise {
    return baseClient.get(`/v4/listings/ebay/status`, { params: { productIds } })
  }

  static getOfferUpListingStatus(productIds): AxiosPromise {
    return baseClient.get(`/v4/listings/offer_up/status`, { params: { productIds } })
  }

  static createScanForm(carrier): AxiosPromise {
    return baseClient.post(`/scan_forms`, { carrier })
  }

  static scanFormIndex(params = {}): AxiosPromise {
    return baseClient.get(`/scan_forms`, { params })
  }

  static getOrders(meta = {}): AxiosPromise {
    return baseClient.get(`/orders`, { params: meta })
  }

  static v5GetOrders(
    params: Partial<IOrdersMetaState> = {},
    cancelToken: CancelToken = undefined
  ): AxiosPromise<{
    orders: API.Order[]
    meta: IOrdersMetaState
  }> {
    return baseClient.get(`/v5/orders`, { params, cancelToken })
  }

  static getOrder(orderId): AxiosPromise {
    return baseClient.get(`/orders/${orderId}`)
  }

  static newShipmentOrdersSearch(meta = {}): AxiosPromise {
    return baseClient.get(`/v5/orders/search`, { params: meta })
  }

  static packingOrdersSearch(meta = {}): AxiosPromise<{
    orders: API.Order[]
    meta: IOrdersMetaState
  }> {
    return baseClient.get(`/v5/orders/packing_search`, { params: meta })
  }

  static getOrderShipments(orderId): AxiosPromise<{ shipments: API.EasyPostLegacyShipment[] }> {
    return baseClient.get(`/orders/${orderId}/shipments`)
  }

  static manifestsIndex(params = {}): AxiosPromise {
    return baseClient.get(`/manifests`, { params })
  }

  static bulkUpdateManifestTrackingState(params: { ids: number[]; state: string }): AxiosPromise<{
    successfulIds: number[]
    errors?: { code: API.BulkUpdateManifestTrackingError; ids: number[] }[]
  }> {
    return baseClient.put(`/v5/manifests/bulk_tracking_state`, params)
  }

  static manifestItemSearch(params = {}): AxiosPromise {
    return baseClient.get(`/manifests/search`, { params })
  }

  static getManifest(id): AxiosPromise {
    return baseClient.get(`/manifests/${id}`)
  }

  static createManifest(params): AxiosPromise {
    return baseClient.post(`/manifests`, params)
  }

  static updateManifest(manifestId, params): AxiosPromise {
    return baseClient.put(`/manifests/${manifestId}`, params)
  }

  static manifestItemsFromQRCode(manifestId, payload): AxiosPromise {
    return baseClient.post(`/manifests/${manifestId}/manifest_items_from_qr_code`, { payload })
  }

  static parseQRCode(payload): AxiosPromise {
    return baseClient.post(`/qr_code_parser/parse`, { payload })
  }

  static bulkDeleteManifests(ids: number[]): AxiosPromise<{
    successfulIds: number[]
    failedIds: number[]
  }> {
    return baseClient.delete(`v4/bulk_destroy/manifests`, { params: { ids } })
  }

  static convertManifestItems(ids, inventoryLocationId): AxiosPromise {
    return baseClient.post(`/v4/manifest_items/convert`, { ids, inventoryLocationId })
  }

  static getManifestScanForm(id): AxiosPromise<{ manifestScanForm: API.ManifestScanForm }> {
    return baseClient.get(`/v5/manifest_scan_forms/${id}`)
  }

  static createManifestScanForm(manifestIds): AxiosPromise<{ manifestScanForm: API.ManifestScanForm }> {
    return baseClient.post(`/v5/manifest_scan_forms`, { manifestIds })
  }

  static relatedEbayProducts(params): AxiosPromise {
    return baseClient.get(`/markets/ebay/products`, { params })
  }

  static listingConfiguration(categoryId): AxiosPromise {
    return baseClient.get(`/v2/listing_configuration?category_id=${categoryId}`)
  }

  static getUnifiedListing(id): AxiosPromise {
    return baseClient.get(`/v2/unified_listings/${id}`)
  }

  static updatetUnifiedListing(id, data): AxiosPromise {
    return baseClient.put(`/v2/unified_listings/${id}`, data)
  }

  static createUnifiedListing(data): AxiosPromise {
    return baseClient.post(`/v2/unified_listings`, data)
  }

  static v4GetOfferUpListings(meta): AxiosPromise {
    return baseClient.get(`/v4/listings/offer_up`, { params: { ...meta } })
  }

  static v4CreateShopgoodwillListing(product): AxiosPromise {
    return baseClient.post('/v4/listings/shopgoodwill', { product })
  }

  static v4GetShopgoodwillListing(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings/shopgoodwill/${listingId}`)
  }

  static v4GetShopgoodwillListingV2(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings_v2/shopgoodwill/${listingId}`)
  }

  static v4ShopgoodwillRequiresAttention(): AxiosPromise {
    return baseClient.get('/v4/requires_attention/shopgoodwill')
  }

  static v4UpdateShopgoodwillListing(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings/shopgoodwill/${listingId}`, { product })
  }

  static v4UpdateShopgoodwillListingV2(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings_v2/shopgoodwill/${listingId}`, { product })
  }

  static v4DeleteShopgoodwillListing(listingId): AxiosPromise {
    return baseClient.delete(`/v4/listings/shopgoodwill/${listingId}`)
  }

  static v4GetShopgoodwillListings(meta): AxiosPromise {
    return baseClient.get(`/v4/listings/shopgoodwill`, { params: { ...meta } })
  }

  static v4GetGoodwillfindsListingV2(
    listingId
  ): AxiosPromise<{ listing: GoodwillfindsListing; product: ProductV4; productListings: RawProductListing[] }> {
    return baseClient.get(`/v4/listings_v2/goodwillfinds/${listingId}`)
  }

  static v4UpdateGoodwillfindsListingV2(
    listingId,
    product
  ): AxiosPromise<{ listing: GoodwillfindsListing; product: ProductV4; productListings: RawProductListing[] }> {
    return baseClient.put(`/v4/listings_v2/goodwillfinds/${listingId}`, { product })
  }

  static v4GetEtsyListingV2(
    listingId
  ): AxiosPromise<{ listing: EtsyListing; product: ProductV4; productListings: RawProductListing[] }> {
    return baseClient.get(`/v4/listings_v2/etsy/${listingId}`)
  }

  static v4CreateEtsyListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/etsy?move_to_market=${moveToMarket}`, { product })
  }

  static v4UpdateEtsyListingV2(
    listingId,
    product
  ): AxiosPromise<{ listing: EtsyListing; product: ProductV4; productListings: RawProductListing[] }> {
    return baseClient.put(`/v4/listings_v2/etsy/${listingId}`, { product })
  }

  static v4GetAmazonListingV2(
    listingId
  ): AxiosPromise<{ listing: AmazonListing; product: ProductV4; productListings: RawProductListing[] }> {
    return baseClient.get(`/v4/listings_v2/amazon/${listingId}`)
  }

  static v4GetDepopListingV2(
    listingId
  ): AxiosPromise<{ listing: DepopListing; product: ProductV4; productListings: RawProductListing[] }> {
    return baseClient.get(`/v4/listings_v2/depop/${listingId}`)
  }

  static v4CreateDepopListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/depop?move_to_market=${moveToMarket}`, { product })
  }

  static v4UpdateDepopListingV2(
    listingId,
    product
  ): AxiosPromise<{ listing: DepopListing; product: ProductV4; productListings: RawProductListing[] }> {
    return baseClient.put(`/v4/listings_v2/depop/${listingId}`, { product })
  }

  static v4CreateAmazonListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/amazon?move_to_market=${moveToMarket}`, { product })
  }

  static v4UpdateAmazonListingV2(
    listingId,
    product
  ): AxiosPromise<{ listing: AmazonListing; product: ProductV4; productListings: RawProductListing[] }> {
    return baseClient.put(`/v4/listings_v2/amazon/${listingId}`, { product })
  }

  static v4EbayRequiresAttention(params: { marketAccountId: number }): AxiosPromise {
    return baseClient.get('/v4/requires_attention/ebay', { params })
  }

  static v4GetEbayListing(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings/ebay/${listingId}`)
  }

  static v4GetEbayListingV2(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings_v2/ebay/${listingId}`)
  }

  static v4UpdateEbayListing(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings/ebay/${listingId}`, { product })
  }

  static v4UpdateEbayListingV2(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings_v2/ebay/${listingId}`, { product })
  }

  static v4DeleteEbayListing(listingId): AxiosPromise {
    return baseClient.delete(`/v4/listings/ebay/${listingId}`)
  }

  static v4GetEbayListings(meta): AxiosPromise {
    return baseClient.get(`/v4/listings/ebay`, { params: { ...meta } })
  }

  static getEbayCategoryFeatures(cmcId): AxiosPromise {
    return baseClient.get(`/v4/ebay/category_market_categories/${cmcId}/category_features`)
  }

  static v4GetShopifyListingV2(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings_v2/shopify/${listingId}`)
  }

  static v4CreateShopifyListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/shopify?move_to_market=${moveToMarket}`, { product })
  }

  static v4CreateGoodwillfindsListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/goodwillfinds?move_to_market=${moveToMarket}`, { product })
  }

  static v4UpdateShopifyListingV2(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings_v2/shopify/${listingId}`, { product })
  }

  static v4ProductsIndex(params): AxiosPromise {
    return baseClient.get('v4/products', { params })
  }

  static duplicateProducts(productIds: number[], numberOfCopies: number): AxiosPromise {
    const productData = {
      productIds,
      numberOfCopies
    }
    return baseClient.post(`v5/products/duplicate`, productData)
  }

  static v5ProductsIndex(params): AxiosPromise {
    return baseClient.get('v5/products', { params })
  }

  static v5ProductsPriceRecommendations(params): AxiosPromise {
    return baseClient.get('v5/products/price_recommendations', { params })
  }

  static v5ListingsRecycle(
    ids,
    marketId,
    recycleProduct = false,
    purgeReason = null,
    dropPayResponsible = null
  ): AxiosPromise {
    return baseClient.put('v5/listings/recycle', { ids, marketId, recycleProduct, purgeReason, dropPayResponsible })
  }

  static v5ProductMove(productId, marketId, form): AxiosPromise {
    return baseClient.put(`v5/products/${productId}/move/${marketId}`, form)
  }

  static v5ProductAddConcurrent(productId, marketId): AxiosPromise {
    return baseClient.put(`v5/products/${productId}/add_concurrent/${marketId}`)
  }

  static v5ProductChangeStrategy(productId, listingStrategy): AxiosPromise {
    return baseClient.put(`v5/products/${productId}/listing_strategy`, { listingStrategy })
  }

  static v4BulkProductUpdate(ids, product): AxiosPromise {
    return baseClient.put(`v4/bulk_update/products`, { ids, product })
  }

  static v4BulkProductUnrecycle(ids): AxiosPromise {
    return baseClient.put(`v4/bulk_unrecycle/products`, { ids })
  }

  static v4BulkProductUpdateBySKU(skus, product): AxiosPromise {
    return baseClient.put(`v4/bulk_update/products`, { skus, product, bySku: true })
  }

  static v4BulkProductUpdateByIdentifier(identifiers, product): AxiosPromise {
    return baseClient.put(`v4/bulk_update/products`, { identifiers, product })
  }

  static v4BulkProductDestroy(params): AxiosPromise {
    return baseClient.delete(`v4/bulk_destroy/products`, { params })
  }

  static getEbayStores(marketAccountId?: number): AxiosPromise {
    return baseClient.get('/v4/ebay/store_categories', { params: { marketAccountId } })
  }

  static bulkReshelf(inventoryLocationId, productIds): AxiosPromise {
    return baseClient.put(`/bulk/reshelf`, {
      inventoryLocationId,
      productIds
    })
  }

  static v4ShopifyRequiresAttention(params): AxiosPromise {
    return baseClient.get('/v4/requires_attention/shopify', { params })
  }

  static v4GetShopifyListings(meta): AxiosPromise {
    return baseClient.get(`/v4/listings_v2/shopify`, { params: { ...meta } })
  }

  static v4GetShopifyListing(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings_v2/shopify/${listingId}`)
  }

  static v4OfferUpRequiresAttention(): AxiosPromise {
    return baseClient.get('/v4/requires_attention/offer_up')
  }

  static bulkRelistOfferUp(ids, offerUpListing): AxiosPromise {
    return baseClient.put(`v4/relistings/offer_up`, { ids, offerUpListing })
  }

  static bulkListOfferUp(ids): AxiosPromise {
    return baseClient.post(`v4/bulk_list/offer_up`, { ids })
  }

  static bulkEndListingsOfferUp(ids): AxiosPromise {
    return baseClient.delete(`v4/bulk_end/offer_up`, { params: { ids } })
  }

  static bulkEndListings(channel: { id: number; name: string }, ids: string[]): AxiosPromise {
    const isMarketplace = ['etsy', 'goodwillfinds', 'amazon', 'depop'].includes(channel.name.toLowerCase())

    const params = isMarketplace ? { ids, marketId: channel.id } : { ids }

    const url = isMarketplace ? `v4/bulk_end/marketplace` : `v4/bulk_end/${channel.name}`

    return baseClient.delete(url, { params })
  }

  static bulkList(channelId, ids): AxiosPromise {
    return baseClient.post(`v4/bulk_list/marketplace`, { ids, marketId: channelId })
  }

  static bulkRelist(channel, ids, extraParams = {}): AxiosPromise {
    const listingContainerKey = `${channel.name.toLowerCase()}_listing`

    const payload = {
      ids,
      market_id: channel.id,
      [listingContainerKey]: extraParams
    }

    return baseClient.put(`v4/relistings/marketplace`, payload)
  }

  static bulkListShopgoodwill(ids): AxiosPromise {
    return baseClient.post(`v4/bulk_list/shopgoodwill`, { ids })
  }

  static bulkPurgeShopgoodwill(ids, shopgoodwillListing): AxiosPromise {
    return baseClient.put(`v4/bulk_purge/shopgoodwill`, { ids, shopgoodwillListing })
  }

  static bulkRelistShopgoodwill(ids, shopgoodwillListing): AxiosPromise {
    return baseClient.put(`v4/relistings/shopgoodwill`, { ids, shopgoodwillListing })
  }

  static bulkListEbay(ids): AxiosPromise {
    return baseClient.post(`v4/bulk_list/ebay`, { ids })
  }

  static bulkRelistEbay(ids, ebayListing): AxiosPromise {
    return baseClient.put(`v4/relistings/ebay`, { ids, ebayListing })
  }

  static bulkEndListingsEbay(ids): AxiosPromise {
    return baseClient.delete(`v4/bulk_end/ebay`, { params: { ids } })
  }

  static bulkEndListingsShopgoodwill(ids): AxiosPromise {
    return baseClient.delete(`v4/bulk_end/shopgoodwill`, { params: { ids } })
  }

  static v4CreateOfferUpListing(product): AxiosPromise {
    return baseClient.post('/v4/listings/offer_up', { product })
  }

  static v4GetOfferUpListing(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings/offer_up/${listingId}`)
  }

  static v4GetOfferUpListingV2(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings_v2/offer_up/${listingId}`)
  }

  static v4UpdateOfferUpListing(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings/offer_up/${listingId}`, { product })
  }

  static v4UpdateOfferUpListingV2(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings_v2/offer_up/${listingId}`, { product })
  }

  static v4DeleteOfferUpListing(listingId): AxiosPromise {
    return baseClient.delete(`/v4/listings/offer_up/${listingId}`)
  }

  static v4FacebookRequiresAttention(): AxiosPromise {
    return baseClient.get('/v4/requires_attention/facebook')
  }

  static v4GetFacebookListings(meta): AxiosPromise {
    return baseClient.get(`/v4/listings/facebook`, { params: { ...meta } })
  }

  static bulkListFacebook(ids): AxiosPromise {
    return baseClient.post(`v4/bulk_list/facebook`, { ids })
  }

  static bulkEndListingsFacebook(ids): AxiosPromise {
    return baseClient.delete(`v4/bulk_end/facebook`, { params: { ids } })
  }

  static bulkRelistFacebook(ids, facebookListing): AxiosPromise {
    return baseClient.put(`v4/relistings/facebook`, { ids, facebookListing })
  }

  static v4GetFacebookListing(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings/facebook/${listingId}`)
  }

  static v4GetFacebookListingV2(listingId): AxiosPromise {
    return baseClient.get(`/v4/listings_v2/facebook/${listingId}`)
  }

  static v4CreateFacebookListing(product): AxiosPromise {
    return baseClient.post(`/v4/listings/facebook`, { product })
  }

  static v4UpdateFacebookListing(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings/facebook/${listingId}`, { product })
  }

  static v4UpdateFacebookListingV2(listingId, product): AxiosPromise {
    return baseClient.put(`/v4/listings_v2/facebook/${listingId}`, { product })
  }

  static bulkListShopify(ids, shopifyListingForm?): AxiosPromise {
    const data: { ids: number[]; shopifyListing?: any } = { ids }
    if (shopifyListingForm) {
      data.shopifyListing = shopifyListingForm
    }
    return baseClient.post(`v4/bulk_list/shopify`, data)
  }

  static bulkEndListingsShopify(ids): AxiosPromise {
    return baseClient.delete(`v4/bulk_end/shopify`, { params: { ids } })
  }

  static bulkRelistShopify(ids, shopifyListing): AxiosPromise {
    return baseClient.put(`v4/relistings/shopify`, { ids, shopifyListing })
  }

  static salesByPeriod(params): AxiosPromise {
    return baseClient.get(`/reports/stores/sales_by_period`, { params })
  }

  static storeActivity(type, params): AxiosPromise {
    return baseClient.get(`/reports/manifests/activity.${type}`, { params })
  }

  static storeSalesOverview(type, params): AxiosPromise {
    return baseClient.get(`/reports/stores/sales_overview.${type}`, { params })
  }

  static activityByUser(type, params): AxiosPromise {
    return baseClient.get(`/reports/stores/activity_by_user.${type}`, { params })
  }

  static manifestItems(type, params): AxiosPromise {
    return baseClient.get(`/reports/manifests/items.${type}`, { params })
  }

  static storeItems(type, params): AxiosPromise {
    return baseClient.get(`/reports/stores/items.${type}`, { params })
  }

  static posterActivity(type, params): AxiosPromise {
    return baseClient.get(`/reports/users/poster_activity.${type}`, { params })
  }

  static salesByCategory(params): AxiosPromise {
    return baseClient.get(`/reports/sales_by_category`, { params })
  }

  static getProductV4(productId, params = undefined): AxiosPromise {
    return baseClient.get(`/v4/products/${productId}`, { params })
  }

  static updateProductV4(productId, product): AxiosPromise {
    return baseClient.put(`/v4/products/${productId}`, { product })
  }

  static createListingStrategy(listingStrategy): AxiosPromise {
    return baseClient.post('/v5/listing_strategies', listingStrategy)
  }

  static updateListingStrategy(id, listingStrategy): AxiosPromise {
    return baseClient.put(`/v5/listing_strategies/${id}`, listingStrategy)
  }

  static getListingStrategy(id): AxiosPromise {
    return baseClient.get(`/v5/listing_strategies/${id}`)
  }

  static deleteListingStrategy(id): AxiosPromise {
    return baseClient.delete(`/v5/listing_strategies/${id}`)
  }

  static getListingStrategies(params = {}): AxiosPromise {
    const safeParams = params !== null && typeof params === 'object' ? params : {}
    return baseClient.get('/v5/listing_strategies', { params: safeParams })
  }

  static listingStrategyConfigurationLookup(listingStrategyId, marketId = undefined): AxiosPromise {
    return baseClient.get(`/v5/listing_strategies/configuration_lookup/${listingStrategyId}`, { params: { marketId } })
  }

  static updateListingStrategyRun(id, listingStrategyRun): AxiosPromise {
    return baseClient.put(`/v5/listing_strategy_runs/${id}`, listingStrategyRun)
  }

  static updateRepricing(id, isRepricingPaused): AxiosPromise {
    return baseClient.put(`/v6/products/${id}`, isRepricingPaused)
  }

  static removeListing(channelName, listingId): AxiosPromise {
    return baseClient.put(`/v5/${channelName}/listings/${listingId}/remove`)
  }

  static createProductV5(product): AxiosPromise {
    return baseClient.post('/v5/products', { product })
  }

  static createBook(book): AxiosPromise {
    return baseClient.post('/v5/books', { book })
  }

  static categoryListingConfiguration(
    categoryId,
    params: { marketAccountIds?: number[]; marketIds?: number[] }
  ): AxiosPromise<CategoryListingConfiguration> {
    return baseClient.get(`/v5/categories/${categoryId}/listing_configuration`, { params })
  }

  static duplicateListingStrategy(id): AxiosPromise {
    return baseClient.post(`/v5/listing_strategies/${id}/duplicate`)
  }

  static updateProductListingStrategy(id, listingStrategy): AxiosPromise {
    return baseClient.put(`/v5/products/${id}/listing_strategy`, { listingStrategy })
  }

  static getOrganizationSettingsV5(): AxiosPromise {
    return baseClient.get(`v5/settings/organization`)
  }

  static updateOrganizationSettingsV5(settings): AxiosPromise {
    return baseClient.put(`v5/settings/organization`, { settings })
  }

  static recycleProduct(id, recycle): AxiosPromise {
    return baseClient.put(`v5/products/${id}/recycle`, { product: { recycle } })
  }

  static markProductsAsPrinted(ids, printed): AxiosPromise {
    return baseClient.put(`/v5/products/print`, { ids, printed })
  }

  static bulkPurgeListing(channelName, ids): AxiosPromise {
    return baseClient.put(`/v5/${channelName}/recycle`, { ids })
  }

  static getNotes(resource, resourceId): AxiosPromise {
    return baseClient.get(`v5/notes?resource_type=${resource}&resource_id=${resourceId}`)
  }

  static createNote(note): AxiosPromise {
    return baseClient.post(`v5/notes`, { note })
  }

  static updateNote(noteId, note): AxiosPromise {
    return baseClient.put(`v5/notes/${noteId}`, { note })
  }

  static deleteNote(noteId): AxiosPromise {
    return baseClient.delete(`v5/notes/${noteId}`)
  }

  static getManifestResponses(responseType = 'rejection'): AxiosPromise {
    return baseClient.get(`v5/manifest_responses?response_type=${responseType}`)
  }

  static editManifestResponses(manifestResponses): AxiosPromise {
    return baseClient.put(`v5/manifest_responses`, { manifestResponses })
  }

  static getSharedEbayAccounts(): AxiosPromise<{ marketAccounts: API.SharedEbayAccount[] }> {
    return baseClient.get(`v5/shared_ebay_accounts`)
  }

  static shareEbayAccount(marketAccountId: number, organizationId: number): AxiosPromise<API.SharedEbayAccount> {
    return baseClient.post(`v5/market_accounts/${marketAccountId}/organizations`, { organizationId })
  }

  static unshareEbayAccount(marketAccountId: number, organizationId: number): AxiosPromise<API.SharedEbayAccount> {
    return baseClient.delete(`v5/market_accounts/${marketAccountId}/organizations/${organizationId}`)
  }

  static unlinkEbayAccount(settingId): AxiosPromise {
    return baseClient.delete(`v5/market_accounts/unlink/ebay/${settingId}`)
  }

  static ordersV5(meta = {}): AxiosPromise {
    return baseClient.get(`v5/orders`, { params: meta })
  }

  static resourceFilters(resource): AxiosPromise {
    return baseClient.get(`v5/resource_filters?resource=${resource}`)
  }

  static getResourceFilter(id): AxiosPromise {
    return baseClient.get(`v5/resource_filters/${id}`)
  }

  static createResourceFilter(resourceFilter): AxiosPromise {
    return baseClient.post(`v5/resource_filters`, { resourceFilter })
  }

  static updateResourceFilter(id, resourceFilter): AxiosPromise {
    return baseClient.put(`v5/resource_filters/${id}`, { resourceFilter })
  }

  static deleteResourceFilter(id): AxiosPromise {
    return baseClient.delete(`v5/resource_filters/${id}`)
  }

  static updateOrder(orderId, order): AxiosPromise {
    return baseClient.put(`v5/orders/${orderId}`, { order })
  }

  static cancelOrder(orderId): AxiosPromise {
    return baseClient.put(`v5/orders/${orderId}/cancel`)
  }

  static shippingMethods(): AxiosPromise {
    return baseClient.get(`v5/shipping_methods`)
  }

  static newManifestBarcode(supplierId): AxiosPromise {
    return baseClient.get(`manifests/barcode?supplier_id=${supplierId}`)
  }

  static ordersV5BulkFulfillmentStatusUpdate(ids, orderFulfillmentStatus, filters): AxiosPromise {
    if (ids.length === 0) {
      return baseClient.put(`v5/orders/bulk/fulfillment_status/all`, { orderFulfillmentStatus, filters })
    }

    return baseClient.put(`v5/orders/bulk/fulfillment_status`, {
      ids,
      filters,
      orderFulfillmentStatus
    })
  }

  static ordersV5BulkUpdatePickupStatus(ids, notifyMarketplace, filters): AxiosPromise<{}> {
    if (ids.length === 0) {
      return baseClient.put(`v5/orders/bulk/pickup_status/all`, { filters, notifyMarketplace })
    }

    return baseClient.put(`v5/orders/bulk/pickup_status`, {
      ids,
      notifyMarketplace
    })
  }

  /**
   *
   * @param {*} ids
   * @param {*} status = {archived: true, cancelled: true}
   */
  static ordersV5BulkStatusUpdate(ids, status, filters): AxiosPromise {
    if (ids.length === 0) {
      return baseClient.put(`v5/orders/bulk/status/all`, {
        status,
        filters
      })
    }

    return baseClient.put(`v5/orders/bulk/status`, {
      ids,
      filters,
      status
    })
  }

  static reportsOrdersUnfullfilledCount(params: { filter: string }) {
    return baseClient.get('reports/orders/unfulfilled_count', { params })
  }

  static reportsOrdersSalesSummary(params: { filter: string; calculation_filter: string }): AxiosPromise {
    return baseClient.get('reports/orders/sales_summary', { params })
  }

  static reportsManifestsUnprocessedItemsSummary(): AxiosPromise {
    return baseClient.get('reports/manifests/unprocessed_items_summary')
  }

  static reportsProductsSummary(): AxiosPromise {
    return baseClient.get('reports/products/summary')
  }

  static reportsUsersPosterActivity(): AxiosPromise {
    return baseClient.get('reports/users/poster_activity')
  }

  static reportsUsersPostingPerformance(): AxiosPromise {
    return baseClient.get('reports/users/posting_performance')
  }

  static reportsStoresSalesSummary(): AxiosPromise {
    return baseClient.get('reports/stores/sales_summary')
  }

  static convertLabel(shipmentId, format): AxiosPromise {
    return baseClient.get(`shipments/${shipmentId}/label.${format}`)
  }

  static storeItemSummary(): AxiosPromise {
    return baseClient.get(`reports/stores/item_summary`)
  }

  static manifestItemsByPeriod(period): AxiosPromise {
    return baseClient.get(`reports/stores/manifest_items_by_period_count?period=${period}`)
  }

  static topItemSales(params): AxiosPromise {
    return baseClient.get(`reports/top_item_sales`, { params })
  }

  static unprocessedManifestItems(supplierId = null): AxiosPromise {
    return baseClient.get(`reports/stores/unprocessed_manifest_items`, { params: { supplierId } })
  }

  static dimWeightCalculator(params: DimWeightCalculatorParams): AxiosPromise<{
    dimWeight: number
  }> {
    return baseClient.post(`dim_weight_calculator`, params)
  }

  static v5ProductsCount(): AxiosPromise {
    return baseClient.get(`v5/products/count`)
  }

  static reportHistory(reportType): AxiosPromise {
    return baseClient.get(`reports/history?type=${reportType}`)
  }

  static generateReport(params): AxiosPromise {
    return baseClient.post(`reports/generate`, params)
  }

  static eventLogs(params): AxiosPromise {
    return baseClient.get(`event_logs`, { params })
  }

  static getWatermark(): AxiosPromise {
    return baseClient.get(`/organizations/settings/watermark`)
  }

  static updateWatermark(params): AxiosPromise {
    return baseClient.put(`/organizations/settings/watermark`, params)
  }

  static previewWatermark(params): AxiosPromise {
    return baseClient.get(`/organizations/settings/watermark/preview`, { params })
  }

  static deleteWatermark(): AxiosPromise {
    return baseClient.delete(`/organizations/settings/watermark`)
  }

  static newSku(supplierId, manualPrefix = null): AxiosPromise {
    return baseClient.get('/sku', { params: { supplierId, manualPrefix } })
  }

  static shippingSettings(): AxiosPromise {
    return baseClient.get('/settings/shipping')
  }

  static updateShippingSettings(settings): AxiosPromise {
    return baseClient.put('/settings/shipping', { settings })
  }

  static markOrderAsFulfilled(orderId, data): AxiosPromise {
    return baseClient.put(`v5/orders/${orderId}/fulfill`, data)
  }

  static getProductListingV5(productId, marketId, marketAccountId?): AxiosPromise {
    return baseClient.get(`/v5/products/${productId}/listings`, { params: { marketId, marketAccountId } })
  }

  static v4CreateShopgoodwillListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/shopgoodwill?move_to_market=${moveToMarket}`, { product })
  }

  static v4CreateOfferUpListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/offer_up?move_to_market=${moveToMarket}`, { product })
  }

  static v4CreateEbayListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/ebay?move_to_market=${moveToMarket}`, { product })
  }

  static v4CreateFacebookListingV2(product, moveToMarket = false): AxiosPromise {
    return baseClient.post(`/v4/listings_v2/facebook?move_to_market=${moveToMarket}`, { product })
  }

  static connectIntegration(integrationName: API.ListingIntegrationName, apiToken): AxiosPromise {
    const urlMap: { [key in API.ListingIntegrationName]: string } = {
      entrupy: `/organizations/settings/item_authentications`,
      hammoq: `/organizations/settings/hammoq_account`
    }
    const url = urlMap[integrationName]

    const params: { apiToken: string; itemAuthenticationName?: string /* only relevant for entrupy */ } = {
      apiToken
    }

    if (integrationName === 'entrupy') {
      params.itemAuthenticationName = integrationName
    }

    return baseClient.post(url, params)
  }

  static disconnectHammoqIntegration(): AxiosPromise {
    return baseClient.delete(`/organizations/settings/hammoq_account`)
  }

  static getEntrupyAuthentications(params: EntrupyAuthenticationParams): AxiosPromise<EntrupyAuthenticationResponse> {
    return baseClient.get(`/item_authentication/entrupy/authentications`, {
      params: {
        ...params,
        entrupyApiVersion: '1.4'
      }
    })
  }

  static getImageProxy(proxiableImageURL): AxiosPromise {
    return baseClient.get(`/image_proxy?url=${proxiableImageURL}`)
  }

  static getConnectedShops(): AxiosPromise {
    return baseClient.get('/market_accounts/shopify')
  }

  static connectShop(shopUrl, apiKey, apiPassword, sharedSecret): AxiosPromise {
    return baseClient.post('/market_accounts/shopify', { shopifyAuth: { shopUrl, apiKey, apiPassword, sharedSecret } })
  }

  static connectGoodwillFinds(accessToken: string): AxiosPromise<{ marketAccount: GoodwillFindsMarketAccount }> {
    return baseClient.post('/market_accounts/goodwillfinds', { accessToken })
  }

  static relinkGoodwillFinds(accessToken: string): AxiosPromise<{ marketAccount: GoodwillFindsMarketAccount }> {
    return baseClient.patch('/market_accounts/goodwillfinds', { accessToken })
  }

  static emailLabel(orderId, emails, shipmentIds): AxiosPromise {
    return baseClient.post('/shipments/email_label', {
      orderId,
      emails,
      shipmentIds
    })
  }

  static updateUserV5(data): AxiosPromise {
    return baseClient.put('/v5/user', data)
  }

  static getManifestPreferredItems(): AxiosPromise<{ manifestPreferredItems: API.ManifestPreferredItem[] }> {
    return baseClient.get('/v5/manifest_preferred_items')
  }

  static createManifestPreferredItem(data): AxiosPromise {
    return baseClient.post('/v5/manifest_preferred_items', data)
  }

  static updateManifestPreferredItem(id, data): AxiosPromise {
    return baseClient.put(`/v5/manifest_preferred_items/${id}`, data)
  }

  static deleteManifestPreferredItem(id): AxiosPromise {
    return baseClient.delete(`/v5/manifest_preferred_items/${id}`)
  }

  static unlockOrderForPicking(orderId: number): AxiosPromise {
    return baseClient.put(`/orders/${orderId}/unlock`)
  }

  static updateOrderItemPickingStatus(orderItemId: number, status: string, assigneeId: number | null): AxiosPromise {
    const data = {
      pickedStatus: status,
      assigneeId
    }

    return baseClient.put(`/v5/orders/order_items/${orderItemId}`, data)
  }

  static bulkUnlockOrdersForPicking(data: {
    orderIds?: string
    filters?: Partial<API.IOrdersMetaState>
  }): AxiosPromise<{ message?: string }> {
    return baseClient.put(`/orders/unlock_bulk`, data)
  }

  static productTags(limit?: number, searchString?: string): AxiosPromise {
    const params = []

    if (limit) {
      params.push(`limit=${limit}`)
    }

    if (searchString) {
      params.push(`search_string=${encodeURIComponent(searchString)}`)
    }

    const queryParameter = constructQueryParamString(params)

    return baseClient.get(`/v5/product_tags${queryParameter}`)
  }

  static createProductTag(title): AxiosPromise {
    return baseClient.post('/v5/product_tags', { productTag: { title } })
  }

  static updateProductTag(id, title): AxiosPromise {
    return baseClient.put(`/v5/product_tags/${id}`, { productTag: { title } })
  }

  static deleteProductTag(id): AxiosPromise {
    return baseClient.delete(`/v5/product_tags/${id}`)
  }

  static manifestItemMerge(merge): AxiosPromise {
    return baseClient.post(`v5/manifest_items/merge`, { merge })
  }

  static manifestItemDivide(divide): AxiosPromise {
    return baseClient.post(`v5/manifest_items/divide`, { divide })
  }

  static manifestItemReject(data): AxiosPromise {
    return baseClient.put(`v5/manifest_items/reject`, data)
  }

  static manifestItemHold(data): AxiosPromise {
    return baseClient.put(`v5/manifest_items/hold`, data)
  }

  static manifestItemMissing(data: { manifestItem: { ids: number[] } }): AxiosPromise<{
    manifest: API.Manifest
    failedIds: number[]
    successfulIds: number[]
  }> {
    return baseClient.put(`v5/manifest_items/missing`, data)
  }

  static manifestItemAuthenticate(data): AxiosPromise {
    return baseClient.put(`v5/manifest_items/authenticate`, data)
  }

  static manifestItemUnprocess(data): AxiosPromise {
    return baseClient.put(`v5/manifest_items/unprocess`, data)
  }

  static convertManifestItemsV5(data): AxiosPromise {
    return baseClient.put(`/v5/manifest_items/convert`, data)
  }

  static authenticateFirstBrands(): AxiosPromise {
    return baseClient.get(`v5/item_authentications/authenticate_first/brands`)
  }

  static itemAuthenticationResync(id): AxiosPromise {
    return baseClient.put(`v5/item_authentications/${id}/resync`)
  }

  static authenticateFirstCategories(brand): AxiosPromise {
    return baseClient.get(`v5/item_authentications/authenticate_first/categories`, { params: { brand } })
  }

  static authenticateFirstChanelCheck(chanelSerial, cancelToken): AxiosPromise {
    return baseClient.get(`v5/item_authentications/authenticate_first/chanel_check`, {
      params: { chanelSerial },
      cancelToken
    })
  }

  static v5CreateItemAuthentication(params): AxiosPromise {
    return baseClient.post(`v5/item_authentications`, params)
  }

  static v5ItemAuthenticationsIndex(params): AxiosPromise {
    return baseClient.get('v5/item_authentications', { params })
  }

  static v5UpdateItemAuthentication(id, params): AxiosPromise {
    return baseClient.put(`v5/item_authentications/${id}`, params)
  }

  static v5GetItemAuthentication(id): AxiosPromise {
    return baseClient.get(`v5/item_authentications/${id}`)
  }

  static getShopifySettings(marketAccountId): AxiosPromise {
    return baseClient.get(`/settings/shopify/${marketAccountId}`)
  }

  static updateShopifySettings(marketAccountId, data): AxiosPromise {
    return baseClient.put(`/settings/shopify/${marketAccountId}`, data)
  }

  static deleteShopifyAccount(marketAccountId): AxiosPromise {
    return baseClient.delete(`/market_accounts/shopify/${marketAccountId}`)
  }

  static resyncShopifyLocations(marketAccountId): AxiosPromise {
    return baseClient.post(`/settings/shopify/${marketAccountId}/resync_locations`)
  }

  static toggleShopifyPublications(marketAccountId, publicationId): AxiosPromise {
    return baseClient.post(`/settings/shopify/${marketAccountId}/toggle_publication/${publicationId}`)
  }

  static resyncShopifyPublications(marketAccountId): AxiosPromise {
    return baseClient.post(`/settings/shopify/${marketAccountId}/resync_publications`)
  }

  static shopifyCollections(marketAccountId): AxiosPromise {
    return baseClient.get('/markets/shopify/collections', { params: { marketAccountId } })
  }

  static getShopifyVendors(marketAccountId): AxiosPromise {
    return baseClient.get(`/shopify_vendors/${marketAccountId}`)
  }

  static getShopifyVendorsSearch(marketAccountId, searchTerm): AxiosPromise {
    return baseClient.get(`/shopify_vendors/${marketAccountId}/search/${searchTerm}`)
  }

  static getOrganizations() {
    return baseClient.get('/organizations')
  }

  static v5GetShopgoodwillListings(meta): AxiosPromise<{ shopgoodwillListings: API.ShopgoodwillListing[] }> {
    return baseClient.get(`/v5/listings/shopgoodwill`, { params: { ...meta } })
  }

  static v5AddAdditionalDetails(
    listingId: number,
    additionalDetails: string,
    channelName: AddAdditionalDetailsChannelNames
  ): AxiosPromise {
    return baseClient.post(`/v5/listings/${channelName}/${listingId}/additional_details`, { additionalDetails })
  }

  static v5GetGoodwillfindsListings(
    marketAccountId: number,
    meta: PaginableMeta
  ): AxiosPromise<{
    listings: API.GoodwillfindsListing[]
    meta: PaginableMeta
  }> {
    return baseClient.get(`/v5/listings/goodwillfinds`, { params: { ...meta, marketAccountId } })
  }

  static v5GetEtsyListings(
    marketAccountId: number,
    meta: PaginableMeta
  ): AxiosPromise<{
    listings: API.EtsyListing[]
    meta: PaginableMeta
  }> {
    return baseClient.get(`/v5/listings/etsy`, { params: { ...meta, marketAccountId } })
  }

  static v5GetAmazonListings(
    marketAccountId: number,
    meta: PaginableMeta
  ): AxiosPromise<{
    listings: API.AmazonListing[]
    meta: PaginableMeta
  }> {
    return baseClient.get(`/v5/listings/amazon`, { params: { ...meta, marketAccountId } })
  }

  static v5GetDepopListings(
    marketAccountId: number,
    meta: Partial<PaginableMeta>
  ): AxiosPromise<{
    listings: API.EtsyListing[]
    meta: PaginableMeta
  }> {
    return baseClient.get(`/v5/listings/depop`, { params: { ...meta, marketAccountId } })
  }

  static bulkListGoodwillfinds(ids): AxiosPromise {
    return baseClient.post(`v4/bulk_list/marketplace`, { ids, marketId: CHANNELS.GOODWILLFINDS.id })
  }

  static bulkEndListingsGoodwillfinds(ids): AxiosPromise {
    const params = { ids, marketId: CHANNELS.GOODWILLFINDS.id }
    return baseClient.delete(`v4/bulk_end/marketplace`, { params })
  }

  static bulkRelistGoodwillfinds(ids, goodwillfindsListing): AxiosPromise {
    const params = { ids, marketId: CHANNELS.GOODWILLFINDS.id, goodwillfindsListing }
    return baseClient.put(`v4/relistings/marketplace`, params)
  }

  /**
   * @deprecated instead use marketRequiresAttention
   */
  static v4GoodwillfindsRequiresAttention(params): AxiosPromise {
    return baseClient.get('/v4/requires_attention/goodwillfinds', { params })
  }

  static marketRequiresAttention(params: { marketAccountId: number | string }): AxiosPromise {
    return baseClient.get('/v4/requires_attention/marketplace', { params })
  }

  static v5GetEbayListings(meta): AxiosPromise {
    return baseClient.get(`/v5/listings/ebay`, { params: { ...meta } })
  }

  static v5GetFacebookListings(meta): AxiosPromise {
    return baseClient.get(`/v5/listings/facebook`, { params: { ...meta } })
  }

  static v5GetShopifyListings(meta): AxiosPromise {
    return baseClient.get(`/v5/listings/shopify`, { params: { ...meta } })
  }

  static productivityUsers(params: { timeStart: string; timeEnd: string; hideInactiveUsers?: boolean }): AxiosPromise<{
    data: API.UserProductivityStat[]
  }> {
    return baseClient.get('/reports/productivity/user', { params })
  }

  static productivityOperational(params: {
    timeStart: string
    timeEnd: string
    interval: string
    userId?: number
    useIsoTimestamp?: boolean
  }): AxiosPromise<{
    data: API.OperationalProductivityStat[]
  }> {
    return baseClient.get('/reports/productivity/operational', { params })
  }

  static createInventoryLocationImport(fileBody, syncWithShopgoodwill): AxiosPromise {
    const formData = new FormData()
    formData.append('data', fileBody)
    formData.append('sync_with_shopgoodwill', syncWithShopgoodwill)

    return Axios({
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      url: '/api/inventory_locations/import'
    })
  }

  static createShippingBoxImport(fileBody, syncWithShopgoodwill): AxiosPromise<{ async_job: { id: number } }> {
    const formData = new FormData()
    formData.append('data', fileBody)
    formData.append('sync_with_shopgoodwill', syncWithShopgoodwill)

    return Axios({
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      url: '/api/shipping_boxes/import'
    })
  }

  static createSupplierImport(fileBody): AxiosPromise<{ async_job: { id: number } }> {
    const formData = new FormData()
    formData.append('data', fileBody)

    return Axios({
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      url: '/api/suppliers/import'
    })
  }

  static v5RemoveCategoryShow(categoryId): AxiosPromise {
    return baseClient.get(`/v5/categories/${categoryId}/remove`)
  }

  static v5RemoveCategory(categoryId): AxiosPromise {
    return baseClient.delete(`/v5/categories/${categoryId}/remove`)
  }

  static v5UpdateOrderShippingAddress(orderId, data): AxiosPromise {
    return baseClient.put(`/v5/orders/${orderId}/shipping_address`, { order: data })
  }

  static v5GetUserPermissions(): AxiosPromise<{ userPermissions: API.UserPermission[] }> {
    return baseClient.get(`/v5/user_permissions`)
  }

  static v5GetRoles(includeUserCount: boolean): AxiosPromise<{ roles: API.Role[] }> {
    const params = { includeUserCount }
    return baseClient.get(`/v5/roles`, { params })
  }

  static v5GetRole(
    id: string,
    params: { includeUsers: boolean }
  ): AxiosPromise<{
    role: API.Role
    users: API.User[]
  }> {
    return baseClient.get(`/v5/roles/${id}`, { params })
  }

  static v5CreateRole(role: API.Role): AxiosPromise<{ role: API.Role }> {
    return baseClient.post(`/v5/roles`, role)
  }

  static v5UpdateRole(role: API.Role): AxiosPromise<{ role: API.Role }> {
    return baseClient.put(`/v5/roles/${role.id}`, role)
  }

  static v5DestroyRole({
    roleId,
    reassignRoleId
  }: {
    roleId: number
    reassignRoleId: number
  }): AxiosPromise<{ role: API.Role }> {
    const params = { reassignRoleId }
    return baseClient.delete(`/v5/roles/${roleId}`, { params })
  }

  static bulkEnableUsers(userIds: number[]): AxiosPromise<{}> {
    return baseClient.put(`users/bulk/enable`, { userIds })
  }

  static bulkDisableUsers(userIds: number[]): AxiosPromise<{}> {
    return baseClient.put(`users/bulk/disable`, { userIds })
  }

  static bulkUpdateUserRoles(userIds: number[], roleId: number): AxiosPromise<{}> {
    return baseClient.put(`/users/bulk/role`, { userIds, roleId })
  }

  static pack(orderId: number, orderItems: PackableItem[], shipments: API.PackedShipment[]): AxiosPromise {
    return baseClient.put(`/v5/orders/${orderId}/pack`, { orderItems, shipments })
  }

  static embeddedListingsSettings(): AxiosPromise<API.EmbeddedListingsSettings> {
    return baseClient.get(`/settings/embedded_listings`)
  }

  static createEmbeddedListingsSettings(): AxiosPromise<API.EmbeddedListingsSettings> {
    return baseClient.post(`/settings/embedded_listings`)
  }

  static updateEmbeddedListingsSettings(settings: API.EmbeddedListingsSettings) {
    return baseClient.put(`/settings/embedded_listings`, { settings })
  }

  static SQSFeedSettings(): AxiosPromise<API.SQSFeedSettings> {
    return baseClient.get(`/settings/sqs_feed`)
  }

  static createSqsFeedSettings(): AxiosPromise<API.SQSFeedSettings> {
    return baseClient.post(`/settings/sqs_feed`)
  }

  static removeSqsFeedSettings(): AxiosPromise {
    return baseClient.delete(`/settings/sqs_feed`)
  }

  static getScheduledCronJobsTime(): AxiosPromise<API.AdminCronJobResponse> {
    return baseClient.get(`/settings/schedule_cron_jobs`)
  }

  static createScheduledCronJob(data): AxiosPromise {
    return baseClient.post(`/settings/schedule_cron_jobs`, data)
  }

  static deleteScheduledCronJob(jobId): AxiosPromise {
    return baseClient.delete(`/settings/schedule_cron_jobs/${jobId}`)
  }

  static v5PendingListingCount(): AxiosPromise {
    return baseClient.get(`/v5/listings/pending_count`)
  }

  static v5AttentionRequiredListingCount(): AxiosPromise {
    return baseClient.get(`/v5/listings/attention_required_count`)
  }

  static v6GetMediaSettings(): AxiosPromise<{
    mediaSettings: API.MediaSettings
  }> {
    return baseClient.get(`/v6/settings/media`)
  }

  static v5EbayListingErrors(id: number): AxiosPromise {
    return baseClient.get(`/v5/listings/${id}/ebay_errors`)
  }

  static v5ShopgoodwillListingErrors(): AxiosPromise {
    return baseClient.get('/v5/listings/sgw_errors')
  }

  static v5ShopifyListingErrors(id: number): AxiosPromise {
    return baseClient.get(`/v5/listings/${id}/shopify_errors`)
  }

  static v5GoodwillfindsListingErrors(marketAccountId: number): AxiosPromise {
    return baseClient.get(`/v5/listings/${marketAccountId}/market_errors`)
  }

  static v5EtsyListingErrors(marketAccountId: number): AxiosPromise {
    return baseClient.get(`/v5/listings/${marketAccountId}/etsy_errors`)
  }

  static v5AmazonListingErrors(marketAccountId: number): AxiosPromise {
    return baseClient.get(`/v5/listings/${marketAccountId}/amazon_errors`)
  }

  static v5DepopListingErrors(marketAccountId: number): AxiosPromise {
    return baseClient.get(`/v5/listings/${marketAccountId}/depop_errors`)
  }

  static hammoqImport(productId: number): AxiosPromise {
    return baseClient.post(`hammoq/import?product_id=${productId}`)
  }

  static sidekiqBatchStatus(batchId: string): AxiosPromise {
    return baseClient.get(`sidekiq_batch_status/${batchId}.json`)
  }

  static addProductAttributeOverrides(productIds: string[], overrideAttributes: API.ProductOverride): AxiosPromise {
    return baseClient.post('/v5/products/add_overrides', { ...overrideAttributes, productIds })
  }

  static removeProductAttributeOverrides(productIds: string[] | number[]): AxiosPromise {
    return baseClient.post('/v5/products/remove_overrides', { productIds })
  }

  static updateEtsySettings(
    id,
    params: { settings: API.EtsySettings; account: API.EtsyMarketAccount }
  ): AxiosPromise<{ settings: API.EtsySettings; account: API.EtsyMarketAccount }> {
    return baseClient.put(`/settings/etsy/${id}`, params)
  }

  static updateDepopSettings(
    id,
    params: { settings: API.DepopSettings; account: API.DepopMarketAccount }
  ): AxiosPromise<{ settings: API.DepopSettings; account: API.DepopMarketAccount }> {
    return baseClient.put(`/settings/depop/${id}`, params)
  }

  static unlinkDepopAccount(settingId): AxiosPromise {
    return baseClient.delete(`v5/market_accounts/unlink/depop/${settingId}`)
  }

  static unlinkEtsyAccount(settingId): AxiosPromise {
    return baseClient.delete(`v5/market_accounts/unlink/etsy/${settingId}`)
  }

  static updateAmazonSettings(
    id,
    params: { settings: API.AmazonSettings; account: API.AmazonMarketAccount }
  ): AxiosPromise<{ settings: API.AmazonSettings; account: API.AmazonMarketAccount }> {
    return baseClient.put(`/settings/amazon/${id}`, params)
  }

  static unlinkAmazonAccount(settingId): AxiosPromise {
    return baseClient.delete(`v5/market_accounts/unlink/amazon/${settingId}`)
  }

  // v6 routes
  // Supports multiple inventory units
  static getProductV6(productId, params = undefined): AxiosPromise {
    return baseClient.get(`/v6/products/${productId}`, { params })
  }

  // Supports updating inventory units
  static updateProductV6(productId, data): AxiosPromise {
    return baseClient.put(`/v6/products/${productId}`, data)
  }

  static getCategoryMapping(highestSalesRankCategory: string): AxiosPromise {
    return baseClient.get('/v5/books/category_mapping', {
      params: { highest_sales_rank_category: highestSalesRankCategory }
    })
  }

  static saveMediaSettings(settings: API.MediaSettings): AxiosPromise {
    return baseClient.put('v5/settings/media', settings)
  }

  static getMediaSettings(): AxiosPromise<{ settingsMedia: API.MediaSettings }> {
    return baseClient.get('v5/settings/media')
  }

  static setAccountAsMedia(accountId: number): AxiosPromise {
    return baseClient.patch(`market_accounts/set_account_as_media/?id=${accountId}`)
  }

  static getAccountMediaFlag(accountId: number): AxiosPromise<{ isMediaAccount: boolean }> {
    return baseClient.get(`market_accounts/get_account_media_flag?id=${accountId}`)
  }
}

export default Client
